/**
 * `engine/pads/engine.ts` — the PADS screen's audio, and the only `AudioContext` in the app.
 *
 * This module owns one context, one bus, one echo tank and one buffer cache. `voices.ts`
 * owns the synthesis; this owns *when* and *whether* anything sounds.
 *
 * ## It never meets the deck, and that is the whole architecture
 *
 * `engine/element.ts` holds the app's single `HTMLAudioElement` and its docblock explains
 * why there is exactly one. This context is a **second, independent output**: the pads
 * play at the same time as the tune, and the two graphs never touch. The hard rule, which
 * `design-direction.md` §9.6 restates so it cannot be eroded:
 *
 * > **Never call `ctx.createMediaElementSource(audio)`** — not once, not behind a flag.
 *
 * Routing the deck into this graph would need `crossOrigin` and CORS on the element,
 * which `spec.md` §13.2 deliberately refuses; the graph would then output silence and the
 * failure would look like a bug in here. It is also why the echo is on the pads and never
 * on the tune, which the screen prints as `SEND` so it does not lie about what it touches.
 *
 * ## The order inside the first gesture is load-bearing
 *
 * `engine/unlock.ts` opens with "ORDER IS LOAD-BEARING. Do not add an await above
 * `el.play()`" and the same applies here for the same reason: a webview grants the
 * gesture's audio privilege to the synchronous part of the handler, and an `await` above
 * `new AudioContext()` spends it. So {@link ensurePadEngine} is a **plain synchronous
 * function**, called first thing inside `pointerdown`, and `resume()` is fired and
 * deliberately not awaited.
 *
 * ## Four things that were measured, not guessed
 *
 *  1. **Schedule at `currentTime + SCHEDULE_LEAD`, never at `currentTime`.** The current
 *     render quantum is already in flight and the attack gets clipped.
 *  2. **Every cached buffer is built on the gesture that creates the context**, via
 *     `warmVoiceCaches`. Doing it lazily measured ~19 ms of main-thread jank on the first
 *     tap — on the tap the user is judging the feature by.
 *  3. **The echo tank is wet-only.** As an insert (the siren, the laser) dry+wet is
 *     right; as a send the dry path doubles the signal and reads as +6 dB of level rather
 *     than as an echo. See `voices.ts`'s `tapeEcho`.
 *  4. **Switching the echo off winds the feedback down as well as closing the send.**
 *     Closing the send alone leaves 0.68 of feedback ringing for about four seconds:
 *     a correct dub tail, and the wrong answer to a switch.
 *
 * ## Latency is the feature
 *
 * The target is 80 ms touch-to-audible on a 2019 mid Android inside Telegram's webview.
 * Nothing in {@link firePad} allocates what could have been allocated earlier, nothing
 * routes through React state, and library sounds are fetched and decoded on screen entry
 * rather than on the tap that needs them. A pad that fires 150 ms after the finger is a
 * toy, and no amount of design fixes it.
 */

import type { PadAssignment, PadSlot, PadSound, SynthVoice } from '@bl/shared';
import { SYNTH_PREFIX } from '@bl/shared';

import type { PadBus, EchoTank, SirenReadout, SirenVoice } from './voices.js';
import {
  bassDrop,
  bus,
  cowbell,
  gain,
  lickShot,
  oneDropKick,
  oneDropSnare,
  setSiren,
  startSiren,
  stopSiren,
  tapeEcho,
  tapeStop,
  tom,
  warmVoiceCaches,
} from './voices.js';

/* ══ constants ═════════════════════════════════════════════════════════════════════ */

/**
 * Seconds of lead on every scheduled event. 20 ms is one render quantum at 128 frames
 * plus margin: enough that the attack is never clipped, small enough to be under the
 * digitiser's own latency and therefore inaudible as delay.
 */
export const SCHEDULE_LEAD = 0.02;

/**
 * The most voices allowed to sound at once. Sixteen `AudioBufferSourceNode`s through one
 * `GainNode` each is a memcpy and a multiply per quantum — free. The real ceiling is
 * allocating filter chains per hit, which this module does not do; sixteen is a
 * backstop against a stuck finger on a repeating pad, not a performance limit.
 */
export const MAX_VOICES = 16;

/** The echo's repeats while the send is open, and where it is wound to when it closes. */
export const ECHO_FEEDBACK_OPEN = 0.68;
export const ECHO_FEEDBACK_CLOSED = 0.2;

/** An eighth at 90 BPM — the same 333 ms the UI's `--t8` animates on. */
export const ECHO_DELAY_S = 0.333;

/* ══ types ═════════════════════════════════════════════════════════════════════════ */

/** Every synth voice, by the name that follows `synth:` in an assignment. */
const SYNTH_TABLE: Readonly<
  Record<SynthVoice, (c: BaseAudioContext, d: AudioNode, t: number) => number>
> = Object.freeze({
  shot: lickShot,
  'one-drop': oneDropKick,
  snare: oneDropSnare,
  tom,
  cowbell,
  bass: bassDrop,
  brake: tapeStop,
});

/** What {@link firePad} did, so the screen can light the pad for exactly as long. */
export interface PadHit {
  /** The sound's own length in seconds — the pad stays inked for this long. */
  readonly durationS: number;
  /** `false` when the slot is empty, or names a sound that has not decoded yet. */
  readonly sounded: boolean;
}

const SILENT: PadHit = Object.freeze({ durationS: 0, sounded: false });

/** The live engine. One per screen mount; {@link disposePadEngine} tears it down. */
interface PadEngine {
  readonly ctx: AudioContext;
  readonly padBus: PadBus;
  readonly echo: EchoTank;
  /** The pads' send into {@link echo}. `0` closed, `1` open. */
  readonly send: GainNode;
  /** Decoded library sounds, by sound id. */
  readonly buffers: Map<string, AudioBuffer>;
  /** Sources still sounding, oldest first. Trimmed to {@link MAX_VOICES}. */
  readonly voices: AudioBufferSourceNode[];
  siren: SirenVoice | null;
}

let engine: PadEngine | null = null;

/* ══ construction ══════════════════════════════════════════════════════════════════ */

/**
 * Tell WebKit this page's audio is primary content, not ambience.
 *
 * **This is the fix for "the pads only make a sound once a track is playing."** On iOS the
 * page gets one `AVAudioSession`, and WebKit picks its category from what it sees: a page
 * whose only output is a Web Audio graph has historically been given an **Ambient**-class
 * session, which is silenced by the ring switch and ducked to nothing. Starting the deck's
 * `<audio>` element promotes the page to a Playback session — which is exactly why the
 * pads came alive the moment a tune started and were silent before it, and why the bug was
 * invisible on a laptop.
 *
 * `navigator.audioSession.type = 'playback'` states the category outright instead of
 * letting it be inferred. Safari 16.4+; absent everywhere else, where it is a no-op and
 * the whole question does not arise. Set **before** the context is constructed, because
 * the session is chosen when the first audio object appears.
 */
function claimPlaybackSession(): void {
  const session = (navigator as unknown as { audioSession?: { type?: string } }).audioSession;
  if (session === undefined) return;
  try {
    session.type = 'playback';
  } catch {
    /* An unexpected value or a read-only property: not worth failing a tap over. */
  }
}

/**
 * The context, created on first call and reused after.
 *
 * Safe to call **on mount**, before any gesture, and the screen does: a context may be
 * *constructed* without a gesture (it simply starts suspended) and `decodeAudioData`
 * works on a suspended one. That is what lets the whole library be decoded before the
 * first pad is touched, instead of the first press of nine of the sixteen pads landing on
 * a buffer that has not arrived. Only **resuming** needs the gesture, and
 * {@link resumePadEngine} does that.
 *
 * Returns `null` when the platform has no `AudioContext` at all, which is a state the
 * screen renders rather than throws on: the pads are then silent and the screen says so.
 *
 * No explicit `sampleRate` is passed. A value that does not match the device's own forces
 * a resampler into the output path, which costs latency for nothing.
 */
export function ensurePadEngine(): PadEngine | null {
  if (engine !== null) return engine;

  const Ctor: typeof AudioContext | undefined =
    typeof globalThis.AudioContext === 'function'
      ? globalThis.AudioContext
      : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (Ctor === undefined) return null;

  claimPlaybackSession();
  const ctx = new Ctor({ latencyHint: 'interactive' });

  const master = gain(ctx, 1);
  master.connect(ctx.destination);

  const padBus = bus(ctx, master);
  const echo = tapeEcho(ctx, ctx.currentTime, {
    delay: ECHO_DELAY_S,
    feedback: ECHO_FEEDBACK_OPEN,
    tone: 2200,
    wetOnly: true,
  });
  echo.out.connect(master);

  const send = gain(ctx, 0);
  padBus.out.connect(send);
  send.connect(echo.input);

  /* ~19 ms of buffer building, spent here rather than on the first pad. */
  warmVoiceCaches(ctx);

  engine = { ctx, padBus, echo, send, buffers: new Map(), voices: [], siren: null };
  return engine;
}

/**
 * Resume the context. **Must be called synchronously inside a real user gesture**, with
 * nothing awaited above it — see the module header.
 *
 * Split from {@link ensurePadEngine} so construction and decoding can happen on mount and
 * only the one operation that genuinely needs a gesture waits for one. Idempotent: a
 * backgrounded webview suspends the context again, so every pointer-down calls this.
 */
export function resumePadEngine(): void {
  const live = engine;
  if (live === null) return;
  if (live.ctx.state === 'running') return;
  /* Fired, not awaited. */
  void live.ctx.resume();
}

/** The engine if it exists. Never creates one — for callers outside a gesture. */
export function padEngine(): PadEngine | null {
  return engine;
}

/**
 * Stop everything and drop the context.
 *
 * Called on unmount. A held siren is stopped first: leaving one running would keep
 * sounding after the screen is gone, and `close()` on a context with a live oscillator
 * is the one way to get a click out of this graph.
 */
export function disposePadEngine(): void {
  const live = engine;
  if (live === null) return;
  engine = null;

  if (live.siren !== null) stopSiren(live.ctx, live.siren);
  for (const source of live.voices) {
    try {
      source.stop();
    } catch {
      /* already stopped, or never started — either way there is nothing to stop */
    }
  }
  live.voices.length = 0;
  live.echo.feedback.gain.value = 0;
  for (const modulator of live.echo.modulators) {
    try {
      modulator.stop();
    } catch {
      /* idem */
    }
  }
  void live.ctx.close();
}

/* ══ the library ═══════════════════════════════════════════════════════════════════ */

/**
 * Fetch and decode library sounds into the cache.
 *
 * Call on screen entry, behind `requestIdleCallback`, and never from inside a tap. Each
 * sound is independent: one 404 or one undecodable file costs that pad and nothing else,
 * so the return value is the ids that failed rather than a rejected promise.
 *
 * `decodeAudioData` is given a fresh `ArrayBuffer` per sound because it **detaches** the
 * one it is handed — a shared buffer would leave every later decode reading zero bytes.
 */
export async function loadPadSounds(
  sounds: readonly PadSound[],
  mediaUrl: (file: string) => string,
): Promise<readonly string[]> {
  const live = engine;
  if (live === null) return sounds.map((sound) => sound.id);

  const failed: string[] = [];
  await Promise.all(
    sounds.map(async (sound) => {
      if (live.buffers.has(sound.id)) return;
      try {
        const response = await fetch(mediaUrl(sound.file));
        if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
        const bytes = await response.arrayBuffer();
        const buffer = await live.ctx.decodeAudioData(bytes);
        live.buffers.set(sound.id, buffer);
      } catch {
        failed.push(sound.id);
      }
    }),
  );
  return failed;
}

/** `true` when this assignment can sound right now. Drives the pad's empty state. */
export function isPadReady(assignment: PadAssignment): boolean {
  if (assignment === null) return false;
  if (assignment.startsWith(SYNTH_PREFIX)) {
    return assignment.slice(SYNTH_PREFIX.length) in SYNTH_TABLE;
  }
  return engine?.buffers.has(assignment) ?? false;
}

/* ══ firing ════════════════════════════════════════════════════════════════════════ */

/** Drop the oldest voices until the cap holds. */
function reapVoices(live: PadEngine): void {
  while (live.voices.length >= MAX_VOICES) {
    const oldest = live.voices.shift();
    if (oldest === undefined) break;
    try {
      oldest.stop();
    } catch {
      /* it finished on its own, which is the common case */
    }
  }
}

/**
 * Play whatever sits on this slot, and return how long it will sound for.
 *
 * Safe to call before the engine exists — it returns {@link SILENT} rather than throwing,
 * because a tap that lands in the same frame as the mount is a real sequence on a phone
 * and it must not be an error.
 *
 * `slot` is taken but unused by the audio: it is here so the caller cannot pass an
 * assignment without saying which pad it came from, which is what makes the readout and
 * the lit pad agree.
 */
export function firePad(slot: PadSlot, assignment: PadAssignment): PadHit {
  void slot;
  const live = engine;
  if (live === null || assignment === null) return SILENT;

  const at = live.ctx.currentTime + SCHEDULE_LEAD;

  if (assignment.startsWith(SYNTH_PREFIX)) {
    const voice = assignment.slice(SYNTH_PREFIX.length);
    const recipe = (
      SYNTH_TABLE as Record<
        string,
        ((c: BaseAudioContext, d: AudioNode, t: number) => number) | undefined
      >
    )[voice];
    if (recipe === undefined) return SILENT;
    return { durationS: recipe(live.ctx, live.padBus.input, at), sounded: true };
  }

  const buffer = live.buffers.get(assignment);
  if (buffer === undefined) return SILENT;

  reapVoices(live);
  const source = live.ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(live.padBus.input);
  source.onended = (): void => {
    const at2 = live.voices.indexOf(source);
    if (at2 >= 0) live.voices.splice(at2, 1);
  };
  source.start(at);
  live.voices.push(source);

  return { durationS: buffer.duration, sounded: true };
}

/* ══ the siren ═════════════════════════════════════════════════════════════════════ */

/** Start the held siren. Returns `false` when there is no engine to start it on. */
export function sirenDown(): boolean {
  const live = engine;
  if (live === null) return false;
  if (live.siren !== null) stopSiren(live.ctx, live.siren);
  live.siren = startSiren(live.ctx, live.padBus.input, live.ctx.currentTime + SCHEDULE_LEAD);
  return true;
}

/**
 * Sweep the held siren. `x` and `y` are 0..1 across the bar — pitch and warble rate.
 *
 * Returns the readout so the screen can print it without reading the graph back, which
 * would mean a render per pointer move.
 */
export function sirenMove(x: number, y: number): SirenReadout | null {
  const live = engine;
  if (live === null || live.siren === null) return null;
  return setSiren(live.ctx, live.siren, x, y);
}

/** Release the siren. Idempotent: every exit path may call it. */
export function sirenUp(): void {
  const live = engine;
  if (live === null || live.siren === null) return;
  stopSiren(live.ctx, live.siren);
  live.siren = null;
}

/** `true` while a siren voice is sounding. The bar's `data-live`. */
export function sirenIsLive(): boolean {
  return engine?.siren !== null && engine !== null;
}

/* ══ the sends ═════════════════════════════════════════════════════════════════════ */

/**
 * Open or close the echo send.
 *
 * Opening is fast (10 ms) and closing is slow (80 ms) — a send that snaps shut clips the
 * repeat that is mid-flight. Closing also winds the **feedback** down, so the tank drains
 * in about three seconds instead of ringing for four: see the module header.
 */
export function setEcho(open: boolean): void {
  const live = engine;
  if (live === null) return;
  const at = live.ctx.currentTime;
  live.send.gain.setTargetAtTime(open ? 1 : 0, at, open ? 0.01 : 0.08);
  live.echo.feedback.gain.setTargetAtTime(
    open ? ECHO_FEEDBACK_OPEN : ECHO_FEEDBACK_CLOSED,
    at,
    0.12,
  );
}
